import { useEffect } from 'react';
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';

// A presented card modal insets the page and leaves a dark backdrop behind the
// status bar, so the bar needs light content whatever the app's colour scheme is.
const CARD_MODAL_SELECTOR = 'ion-modal.modal-card.show-modal';

const isIonModal = (node: Node) => node instanceof HTMLElement && node.tagName === 'ION-MODAL';

export const useSystemUiAppearance = () => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || typeof window === 'undefined') {
      return;
    }

    const mediaQuery = 'matchMedia' in window
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : undefined;

    // dedupes bridge calls: mutations arrive far more often than the
    // derived style actually changes
    let appliedStyle: SystemBarsStyle | undefined;

    const applyAppearance = () => {
      const hasCardModal = !!document.querySelector(CARD_MODAL_SELECTOR);
      const style = hasCardModal || !!mediaQuery?.matches ? SystemBarsStyle.Dark : SystemBarsStyle.Light;

      if (style === appliedStyle) {
        return;
      }

      appliedStyle = style;
      SystemBars.setStyle({ style }).catch((error) => {
        console.warn('Failed to set system bars style', error);
        appliedStyle = undefined;
      });
    };

    // Ionic reflects the presented state through classes on the ion-modal
    // element, and the strip can land several frames after the dismiss
    // events — later still when a dismissal from landscape races the
    // rotation back to portrait. Sampling on the events therefore misses;
    // observing the class mutations themselves cannot.
    const observer = new MutationObserver((mutations) => {
      const touchesModal = mutations.some((mutation) =>
        isIonModal(mutation.target)
        || Array.from(mutation.addedNodes).some(isIonModal)
        || Array.from(mutation.removedNodes).some(isIonModal)
      );

      if (touchesModal) {
        applyAppearance();
      }
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });

    applyAppearance();
    mediaQuery?.addEventListener('change', applyAppearance);

    return () => {
      observer.disconnect();
      mediaQuery?.removeEventListener('change', applyAppearance);
    };
  }, []);
};
